import React, { useCallback, useEffect, useState, useMemo } from 'react';

import Card from './components/AgendaContent/Card';
import PromoCard from './components/AgendaContent/PromoCard';
import EmptyAgenda from './components/AgendaContent/EmptyAgenda';
import Filterbar from './components/AgendaFilters/Filterbar';
import Loader from './components/AgendaContent/Loader';

import {
	today,
	getWeekNumber,
	sortByDate,
	hasMatchingShowTags,
	dbDateToTimestamp,
} from '../agenda/utils/helpers';

/**
	* @description		Core functionality for filtering the agenda
*/
const Filter = ({
	config,
	eventFeed,
	promoData,
	availableLocations,
	availableDates,
	availableTags,
	availableCategories,
	rootElement,
}) => {

	const [filteredElements, setFilteredElements] = useState([]); //result of the filtered data
	const [filteredCount, setFilteredCount] = useState(null);
	const [cards, setCards] = useState([]); //state that holds the rendered components
	const [rendering, setRendering] = useState(true);
	const [showModal, setShowModal] = useState(false);

	//state reflecting params that tweak the filtering
	const [activeFilterDate, setActiveFilterDate] = useState(false); //boolean (false = 'alle dagen'; true = checkout activeTimeStamp)
	const [activeTimestamp, setActiveTimeStamp] = useState(today); //becomes a timestamp when date filtering is used (Only one, not an array)
	const [dateRange, setDateRange] = useState(null); //'month' or 'week' or 'weekend'
	const [activeLocations, setActiveLocations] = useState([]); //empty array or one or more cinema as filter
	const [activeTags, setActiveTags] = useState([]);
	const [activeCategories, setActiveCategories] = useState([]);

	// memo's to avoid unnecessary rerender filterbar
	const memoizedFilteredCount = useMemo(() => filteredCount, [filteredCount]);
	const memoizedActiveFilterDate = useMemo(() => activeFilterDate, [activeFilterDate]);
	const memoizedActiveTimestamp = useMemo(() => activeTimestamp, [activeTimestamp]);
	const memoizedDateRange = useMemo(() => dateRange, [dateRange]);
	const memoizedActiveLocations = useMemo(() => activeLocations, [activeLocations]);
	const memoizedActiveCategories = useMemo(() => activeCategories, [activeCategories]);
	const memoizedActiveTags = useMemo(() => activeTags, [activeTags]);

	// const updateUrl = useCallback(() => {
	// 	const urlParams = new URLSearchParams();
	// 	const activeDay = activeFilterDate ? activeTimestamp : 'all';
	// 	if (activeDay) urlParams.set('f-day', encodeURI(activeDay.toString()));
	// 	if (dateRange) urlParams.set('f-date-range', encodeURI(dateRange.toString()));
	// 	if (activeLocations.length > 0) urlParams.set('f-location', encodeURI(activeLocations.join('+')));
	// 	if (activeTags.length > 0) urlParams.set('f-tag', encodeURI(activeTags.join('+')));
	// 	if (activeCategories.length > 0) urlParams.set('f-category', encodeURI(activeCategories.join('+')));
	// 	window.history.replaceState(null, '', '?' + urlParams.toString());
	// }, [activeFilterDate, activeTimestamp, dateRange, activeLocations, activeCategories, activeTags]);

	const updateUrl = useCallback(() => {
		const urlParams = new URLSearchParams(); // added the window.location.search so the link is preserved as is
		const activeDay = activeFilterDate ? activeTimestamp : 'all';
		if (activeDay) urlParams.set('f-day', encodeURI(activeDay.toString()));
		if (dateRange) urlParams.set('f-date-range', encodeURI(dateRange.toString()));
		if (activeLocations.length > 0) urlParams.set('f-location', encodeURI(activeLocations.map(location => Object.keys(location)).join('+'))); //todo
		if (activeTags.length > 0) urlParams.set('f-tag', encodeURI(activeTags.map(tag => Object.keys(tag)).join('+'))); //todo
		if (activeCategories.length > 0) urlParams.set('f-category', encodeURI(activeCategories.map(category => Object.keys(category)).join('+')));
		window.history.replaceState(null, '', '?' + urlParams.toString());
	}, [activeFilterDate, activeTimestamp, activeLocations, activeCategories, activeTags]);


	const setDateParam = (value) => {
		if (value === 'all') {
			setActiveFilterDate(false);
			setActiveTimeStamp(null);
		} else {
			setActiveFilterDate(true);
			setActiveTimeStamp(value);
		}
  	};

	const setDateRangeParam = (value) => {
		if (['month', 'week', 'weekend', null].includes(value)) setDateRange(value);
	}

	const setLocationsParam = (value) => {
		if (activeLocations.includes(value)) {
			setActiveLocations([...activeLocations].filter((locationOption) => Object.keys(locationOption)[0] !== Object.keys(value)[0]));
		} else {
			setActiveLocations([...activeLocations].concat(value));
		}
  };

	const setTagsParam = useCallback((value, onlyActivate) => {
		if (activeTags.includes(value)) {
			!onlyActivate && setActiveTags([...activeTags].filter((tagOption) => Object.keys(tagOption)[0] !== Object.keys(value)[0]))
		} else {
			setActiveTags([...activeTags].concat(value));
		}
	}, [activeTags]);

	const setCategoriesParam = (value) => {
		if (activeCategories.includes(value)) {
			setActiveCategories([...activeCategories].filter((category) => Object.keys(category)[0] !== Object.keys(value)[0]));
		} else {
			setActiveCategories([...activeCategories].concat(value));
		}
	};

   const resetParams = () => {
		setActiveFilterDate(false);
		setActiveTimeStamp(today);
		setDateRange(null);
		setActiveLocations([]);
		setActiveCategories([]);
		setActiveTags([]);
  	};

	/**
	 * @description	Main filtering functionality, activated by a change in params through useEffect below
	 */
   const filterAccordingToCurrentParams = useCallback((input) => {
		let copyInput = structuredClone(input);

		// filtering elements whether tag exists in the elements tags OR any shows with that tag exist
		let resultTagFilter = [];
		if (activeTags.length > 0) {
			copyInput.forEach((element) => {
				let matchingTag = false;
				activeTags.forEach((tag) => {
					if (Object.keys(element.tags?.interests).indexOf(Object.keys(tag)[0]) > -1) matchingTag = true;
				});
				if (matchingTag) {
					resultTagFilter.push(element);
				} else {
					const matchingShowTags = hasMatchingShowTags(element, config.no_special_tab_tags);
					if (matchingShowTags) resultTagFilter.push(element);
				}
			});
		} else {
			resultTagFilter = structuredClone(copyInput);
		}

		// filtering elements whether category exists in the elements tags
		let resultCategoryFilter = [];
		if (activeCategories.length > 0) {
			resultTagFilter.forEach((element) => {
				let matchingCategory = false;
				activeCategories.forEach((category) => {
					if (Object.keys(element.tags?.categories).indexOf(Object.keys(category)[0]) > -1) matchingCategory = true;
				});
				if (matchingCategory) {
					resultCategoryFilter.push(element);
				}
			});
		} else {
			resultCategoryFilter = structuredClone(resultTagFilter);
		}

		// filtering elements based on date AND return filtered times array for date
		let resultFilterDate = [];
		if (activeFilterDate && activeTimestamp) {

			resultCategoryFilter.forEach((element) => {
				const elementCopy = structuredClone(element);
				if (config.date_filter_range && dateRange) {
					if (dateRange === 'month') {
						elementCopy.times = elementCopy.times.filter((show) => new Date(show.date).getMonth() === new Date(activeTimestamp).getMonth());
					} else if (dateRange === 'week') {
						elementCopy.times = elementCopy.times.filter((show) => getWeekNumber(show.date) === getWeekNumber(activeTimestamp));
					} else if (dateRange === 'weekend') {
						elementCopy.times = elementCopy.times.filter((show) => {
							if (getWeekNumber(show.date) !== getWeekNumber(activeTimestamp)) return show; //future weeks
							if (new Date(show.date).getDay() === 6 || new Date(show.date).getDay() === 7) return show; //this week & weekend
						});
					}
				} else {
					if (config.results_beyond_date) { // Flint
						elementCopy.times = elementCopy.times.filter((show) => show.date >= activeTimestamp);
					} else { //Bioscopen
						elementCopy.times = elementCopy.times.filter((show) => show.date === activeTimestamp);
					}
				}
				resultFilterDate.push(elementCopy);
			});
		} else {
			resultFilterDate = structuredClone(resultCategoryFilter);
		}

		// filtering elements on location (only adjust shows array)
		let resultFilterLocations = [];
		if (activeLocations.length > 0) {
			resultFilterDate.forEach((element) => {
				const elementCopy = structuredClone(element);
				elementCopy.times = elementCopy.times.filter((show) => activeLocations.some(location => Object.keys(location)[0] === Object.keys(show.location)[0]));
				resultFilterLocations.push(elementCopy);
			});
		} else {
			resultFilterLocations = structuredClone(resultFilterDate);
		}

		return resultFilterLocations;
	}, [activeFilterDate, activeTimestamp, activeLocations, activeCategories, activeTags, config, dateRange]);

	/**
	 * @description	Main filter (reacting to a change in params)
	 */
	useEffect(() => {
		if (!eventFeed) return;
		let filterDataObj = structuredClone(eventFeed);
		const resultFiltering = filterAccordingToCurrentParams(sortByDate(filterDataObj.filter((el) => el['times'] && el['times'].length > 0)));
		const elementsWithTimes = resultFiltering.filter((el) => el['times'] && el['times'].length > 0);
		setFilteredCount(elementsWithTimes.length);
		setFilteredElements(elementsWithTimes);

	}, [eventFeed, activeFilterDate, activeTimestamp, activeLocations, activeTags, activeCategories, filterAccordingToCurrentParams]);

	/**
	 * @description	Renders the ReactElement cards based on the filteredElements
	 */
	useEffect(() => {
		setRendering(true);

		const cards = filteredElements.map((element, index) =>
			<Card
				key={index}
				element={element}
				setTagsParam={setTagsParam}
				config={config}
			/>
		);

		if (config.promo_cards_frequency && promoData && promoData.length > 0) {
			const copyPromoData = structuredClone(promoData);
			let index = config.promo_cards_frequency;
			while (index < cards.length) {
				cards.splice(index, 0,
				<div className="promo-container-agenda" onClick={()=>setShowModal(false)}>
					<PromoCard
						key={index}
						values={copyPromoData[0]}
						animationDelay={{ '--animation-delay': `${(index < 4 ? index + 1 : 4) * 100}ms` }}
					/>
				</div>
				);
				index += config.promo_cards_frequency;
				copyPromoData.push(copyPromoData.shift());
			}
		}

		setCards(cards);
		setRendering(false);
  	}, [activeFilterDate, activeTimestamp, config, filteredElements, setTagsParam]);


	/**
	 * @description	Writes the current parameters to the url
	 */
	useEffect(() => {
		if (!rendering) updateUrl();
	}, [activeFilterDate, activeTimestamp, activeLocations, activeTags, activeCategories, rendering, updateUrl]);


	/**
	 * @description	Gets the parameters from url and sets the state accordingly
	 */
	useEffect(() => {
		if (eventFeed && window.location.search) {
			const queryParams = new URLSearchParams(window.location.search);

			if (queryParams.has('f-date-range')) {
				const param = decodeURI(queryParams.get('f-date-range') ?? '');
				if (['month', 'week', 'weekend'].includes(param)) setDateRange(param);
			}

			if (queryParams.has('f-day')) {
				const param = decodeURI(queryParams.get('f-day') ?? '');
				if (param === 'all') {
					setActiveFilterDate(false);
				} else {
					setActiveFilterDate(true);
					setActiveTimeStamp(Number(param));
				}
			}

			if (queryParams.has('f-location')) {
				const slugs = decodeURI(queryParams.get('f-location') ?? '').split('+');
				const params = availableLocations.filter((location) => slugs.includes(Object.keys(location)[0]));
				if (params.length > 0) setActiveLocations(params);
			}

			if (queryParams.has('f-tag')) {
				const slugs = decodeURI(queryParams.get('f-tag') ?? '').split('+');
				const params = availableTags.filter((tag) => slugs.includes(Object.keys(tag)[0]));
				if (params.length > 0) setActiveTags(params);
			}

			if (queryParams.has('f-category')) {
				const slugs = decodeURI(queryParams.get('f-category') ?? '').split('+');
				const params = availableCategories.filter((category => slugs.includes(Object.keys(category)[0])));
				if (params.length > 0) setActiveCategories(params);
			}
		}
	}, [eventFeed, availableLocations, availableDates, availableTags, availableCategories]);

  	if (rendering) return (
		<div className="loader__container--agenda">
			<Loader />
		</div>
	);

	return (
		<>

			<Filterbar
				memoizedFilteredCount={memoizedFilteredCount}

				memoizedActiveFilterDate={memoizedActiveFilterDate}
				memoizedActiveTimestamp={memoizedActiveTimestamp}
				memoizedDateRange={memoizedDateRange}
				memoizedActiveLocations={memoizedActiveLocations}
				memoizedActiveCategories={memoizedActiveCategories}
				memoizedActiveTags={memoizedActiveTags}

				availableDates={availableDates}
				availableLocations={availableLocations}
				availableCategories={availableCategories}
				availableTags={availableTags}

				setDateParam={setDateParam}
				setDateRangeParam={setDateRangeParam}
				setLocationsParam={setLocationsParam}
				setTagsParam={setTagsParam}
				setCategoriesParam={setCategoriesParam}
				resetParams={resetParams}

				config={config}
				showModal={showModal}
				setShowModal={setShowModal}
			/>

			{ cards.length < 1 ? (
				<EmptyAgenda
					text={config.empty_agenda_text}
					subtext={config.empty_agenda_subtext}
				/>
			) : (
				<>
					<ol className={`js_cards-container featured agenda__tiles ${!rendering ? 'animated animated__fadeIn' : 'animated animated__preHidden'}`}>
						{cards}
					</ol>
					{/* <div class="agenda__button">
						<a class="btn agenda__btn" href="https://goudseschouwburg.nl/p8/Kaartverkoop">Meer laden</a>
					</div> */}
				</>
			)}

		</>
	);
};

export default React.memo(Filter);
